import {Injectable} from "@angular/core";
import {Location} from "@angular/common";
import {NavigationEnd, Router, UrlTree} from "@angular/router";
import {timer} from "rxjs";
import {filter, switchMap, take, takeWhile} from "rxjs/operators";
import {AlertService} from "../alert/alert.service";
import {LoggerService} from "../common/logger.service";
import {Constants} from "../constants";
import {UsanaObject} from "../usana-object";
import {EmailVerificationState, IEmailVerificationStatus} from "./email-verification-models";
import {GqlEmailVerificationService} from "./graphql/gql-email-verification.service";

// The shopper arrives mid page-load from their mail client, so the default few seconds can elapse
// before they have read anything. Every alert carries a close button.
const STAYS_UNTIL_DISMISSED: number = -1;

// Resolution is normally sub-second, so a short poll covers it without the shopper waiting on a
// page they did not ask to wait on.
const POLL_INTERVAL_MS: number = 1000;
const MAX_ATTEMPTS: number = 5;

/**
 * Reports the outcome of an email verification link on whatever page the shopper lands on. The
 * destination is chosen by the notification that sent the link, so this watches every navigation
 * rather than belonging to one route.
 */
@Injectable({
    providedIn: 'root'
})
export class EmailVerificationUrlService extends UsanaObject {

    // The locale and default-page rewrites re-navigate carrying the query string, so one arrival
    // reaches this more than once. Keyed on the id rather than on the URL, which changes.
    private readonly displayedStatusIds: Set<string> = new Set<string>();

    constructor(
        private readonly router: Router,
        private readonly location: Location,
        private readonly alertService: AlertService,
        private readonly gqlEmailVerificationService: GqlEmailVerificationService,
        loggerService: LoggerService
    ) {
        super(loggerService);
    }

    init(): void {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            const urlTree: UrlTree = this.router.parseUrl(event.urlAfterRedirects);
            const vti: string = urlTree.queryParams[Constants.EMAIL_VERIFICATION_ID_PARAM];
            if (!vti || this.displayedStatusIds.has(vti)) {
                return;
            }
            this.displayedStatusIds.add(vti);
            this.stripId(urlTree);
            this.showStatus(vti);
        });
    }

    // Keeps the id out of the address bar and out of anything the shopper copies or refreshes.
    private stripId(urlTree: UrlTree): void {
        const {[Constants.EMAIL_VERIFICATION_ID_PARAM]: removed, ...remainingParams} = urlTree.queryParams;
        urlTree.queryParams = remainingParams;
        this.location.replaceState(this.router.serializeUrl(urlTree));
    }

    // Reports once the outcome settles, or once the retries are spent — never mid-poll, so the
    // shopper sees a single alert rather than progress replaced by a result.
    private showStatus(vti: string): void {
        let latest: IEmailVerificationStatus;
        timer(0, POLL_INTERVAL_MS).pipe(
            switchMap(() => this.gqlEmailVerificationService.emailVerificationStatus(vti)),
            takeWhile((status: IEmailVerificationStatus) => !this.isSettled(status), true),
            take(MAX_ATTEMPTS)
        ).subscribe({
            next: (status: IEmailVerificationStatus) => latest = status,
            complete: () => this.alert(latest),
            error: (error: any) => {
                this.log.error("emailVerificationStatus", error);
                this.alertUnavailable();
            }
        });
    }

    private isSettled(status: IEmailVerificationStatus): boolean {
        return status?.state === EmailVerificationState.VERIFIED
            || status?.state === EmailVerificationState.FAILED;
    }

    private alert(status: IEmailVerificationStatus): void {
        if (status?.state === EmailVerificationState.VERIFIED) {
            this.alertService.addSuccess({body: 'cart.email.verification.verified', translate: true}, STAYS_UNTIL_DISMISSED);
        } else if (status?.state === EmailVerificationState.FAILED) {
            this.log.info("emailVerificationStatus", {rejectReason: status.rejectReason});
            this.alertService.addFail(
                {body: 'cart.email.verification.failed.title', translate: true},
                {body: 'cart.email.verification.failed', translate: true},
                true,
                STAYS_UNTIL_DISMISSED);
        } else if (status?.state === EmailVerificationState.PENDING) {
            // The address is held and the write is still being retried, which can outlast any wait
            // worth asking of the shopper.
            this.alertService.addWarning({body: 'cart.email.verification.pending', translate: true}, STAYS_UNTIL_DISMISSED);
        } else {
            // No record of the credential after the retries, so the redeem never reached identity.
            this.alertService.addFail(
                {body: 'cart.email.verification.unconfirmed.title', translate: true},
                {body: 'cart.email.verification.unconfirmed', translate: true},
                true,
                STAYS_UNTIL_DISMISSED);
        }
    }

    // Nothing is in flight when the lookup itself fails, so this must not read as "hang on".
    private alertUnavailable(): void {
        this.alertService.addFail(
            {body: 'cart.email.verification.unavailable.title', translate: true},
            {body: 'cart.email.verification.unavailable', translate: true},
            true,
            STAYS_UNTIL_DISMISSED);
    }
}
